<template>
    <div>
        <loader v-if="isLoading" />
        <div v-else>
            <p v-if="errorMessage !== ''">{{ errorMessage }}</p>
            <div v-else class="list">
                <b-row>
                    <b-col cols="3">
                        <b>Patient (ID):</b>
                    </b-col>
                    <b-col>
                        <b>Species:</b>
                    </b-col>
                    <b-col cols="3">
                        <b>Test:</b>
                    </b-col>
                    <b-col cols="2">
                        <b>Test Date:</b>
                    </b-col>
                    <b-col cols="2">
                        <b>Result Date:</b>
                    </b-col>
                    <b-col cols="1" />
                </b-row>

                <div v-if="list.length > 0">
                    <b-row
                        v-for="record in list"
                        :key="record.id"
                        class="row-record d-flex align-items-center"
                    >
                        <b-col cols="3">
                            <patient-name
                                v-if="
                                    record.patient &&
                                    record.patient.encodedId &&
                                    record.patient.name
                                "
                                :use-router="USE_PATIENT_MODULE_VUE"
                                :patient="record.patient"
                                :text="properPatientName(record)"
                            />
                            <span v-else>
                                {{ properPatientName(record) }}
                            </span>
                        </b-col>
                        <b-col class="text-capitalize">
                            {{ properPatientSpecies(record) }}
                        </b-col>
                        <b-col cols="3" v-html="properTestName(record)">
                        </b-col>
                        <b-col cols="2">
                            {{ properTestDate(record) }}
                        </b-col>
                        <b-col cols="2">
                            {{ properResultDate(record) }}
                        </b-col>
                        <b-col cols="1" class="text-right">
                            <b-dropdown size="sm" variant="link" right no-caret>
                                <template v-slot:button-content>
                                    <i class="cus-cog-down"></i>
                                </template>
                                <template v-if="showPreviewResult(record)">
                                    <b-dropdown-item
                                        v-if="
                                            _hasAccess(
                                                'patient.unassigned.labs.preview.result'
                                            )
                                        "
                                        @click="previewResult(record)"
                                    >
                                        <i class="cus-magnifier"></i>
                                        Preview Result
                                    </b-dropdown-item>
                                </template>
                                <b-dropdown-item @click="assignLab(record)">
                                    <i class="cus-page-white-edit"></i>
                                    Assign Lab
                                </b-dropdown-item>

                                <b-dropdown-item @click="deleteLab(record)">
                                    <i class="cus-delete"></i> Delete Lab
                                </b-dropdown-item>
                            </b-dropdown>
                        </b-col>
                    </b-row>
                </div>
                <div v-else>
                    <b-row class="row-record">
                        <b-col>
                            <i>No records available.</i>
                        </b-col>
                    </b-row>
                </div>
            </div>

            <b-pagination
                class="vet-pagination"
                align="right"
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                prev-text="Prev"
                next-text="Next"
                :hide-goto-end-buttons="true"
                @change="changePage"
            ></b-pagination>
        </div>

        <modal-delete
            ref="modalDelete"
            :reloadAfter="true"
            @reloadlist="fetchList('all', 1)"
        />

        <modal-assign
            ref="modalAssign"
            :reloadAfter="true"
            @reloadlist="fetchList('all', 1)"
        />

        <modal-assign-onprem
            ref="modalAssignOnprem"
            :reloadAfter="true"
            @reload="fetchList('all', 1)"
        />

        <modal-assign-abaxisapi
            ref="modalAssignAbaxisapi"
            :reloadAfter="true"
            @reload="fetchList('all', 1)"
        />

        <modal-assign-lab
            ref="modalAssignLab"
            :reloadAfter="true"
            @reload="fetchList('all', 1)"
        />

        <modal-assign-antech
            ref="modalAssignAntech"
            :reloadAfter="true"
            @reload="fetchList('all', 1)"
        />

        <modal-preview-result ref="modalPreviewResult" @assign="assignLab" />
    </div>
</template>

<script>
import ModalDelete from "./modal-delete";
import ModalAssign from "./modal-assign";
import PatientName from "@/components/commons/patient-name";
import ModalAssignLab from "@/components/unassign-labs/assign-lab";
import ModalAssignAntech from "@/components/unassign-labs/assign-antech";
import ModalAssignOnprem from "@/components/unassign-labs/assign-onprem";
import ModalAssignAbaxisapi from "@/components/unassign-labs/assign-abaxisapi";
import ModalPreviewResult from "@/components/unassign-labs/preview-result";

export default {
    components: {
        ModalDelete,
        ModalAssign,
        PatientName,
        ModalAssignLab,
        ModalAssignAntech,
        ModalAssignOnprem,
        ModalAssignAbaxisapi,
        ModalPreviewResult,
        Loader: () => import("@/components/elements/loader-list")
    },
    props: {
        businessId: {
            type: String,
            required: true
        },
        isUsingViewProfile: {
            type: Boolean,
            required: false,
            default: true
        }
    },
    data() {
        return {
            currentPage: 1,
            totalRows: 0,
            perPage: 0,
            isLoading: true,
            errorMessage: "",
            list: [],
            CIAPPS: process.env.CI_APPS
        };
    },
    watch: {
        businessId(value) {
            if (value !== "") {
                this.fetchList("all", 1);
            }
        }
    },
    mounted() {
        this.fetchList("all", 1);
    },
    methods: {
        async fetchList(filterType, page) {
            if (this.businessId === "") {
                return;
            }

            this.isLoading = true;
            this.errorMessage = "";

            try {
                const res = await this.$http.get(
                    `${process.env.BARRAMUNDI}lab/unassign-lab/${this.businessId}`,
                    {
                        params: {
                            type: filterType,
                            page: page
                        }
                    }
                );

                this.isLoading = false;

                if (res.data.response.message.code === 200) {
                    const { resources, meta } = res.data.response;
                    this.list = resources;

                    this.totalRows = meta.total;
                    this.perPage = meta.perPage;
                    this.currentPage = page;
                } else {
                    throw new Error("Unable to get unassigned lab list.");
                }
            } catch (e) {
                this.isLoading = false;
                this.errorMessage = "Error: " + e.message;
            }
        },
        properPatientName(record = {}) {
            const { animal_name, animal_id, patient } = record;

            if (patient && patient.name) {
                return patient.index
                    ? `${patient.name} (${patient.index})`
                    : patient.name;
            }

            if (animal_name && animal_id) {
                return `${animal_name} (${animal_id})`;
            }

            if (patient && patient.index) {
                return patient.index;
            }

            return null;
        },
        properTestName(record = {}) {
            let testName = "";
            let serviceURL = this.CIAPPS;

            switch (record.module) {
                case "onprem":
                    if (Object.keys(record.test).length > 0) {
                        testName = record.test.name;

                        if (record.test.item) {
                            testName =
                                '<a href="' +
                                serviceURL +
                                "inventory/item/view/" +
                                record.test.item.encodedId +
                                '">' +
                                testName +
                                "</a>";
                        }
                    }

                    if (record.analyzerType) {
                        testName = record.source + ": " + record.analyzerType;
                    }
                    break;

                case "abaxis_api":
                case "abaxis_fuse":
                    if (record.test && Object.keys(record.test).length > 0) {
                        testName = record.test.name;

                        if (record.test.encodedId) {
                            testName =
                                '<a href="' +
                                serviceURL +
                                "inventory/item/view/" +
                                record.test.encodedId +
                                '">' +
                                testName +
                                "</a>";
                        }
                    }
                    break;
                case "lab":
                    if (record.result && record.result.item) {
                        testName = record.result.item.name;
                        if (record.result.item.encodedId) {
                            testName =
                                '<a href="' +
                                serviceURL +
                                "inventory/item/view/" +
                                record.result.item.encodedId +
                                '">' +
                                testName +
                                "</a>";
                        }
                    } else if (Object.keys(record.result).length > 0) {
                        var provider = record.reference_type.replace(/_/g, " ");
                        provider = provider
                            .toLowerCase()
                            .replace(/\b[a-z]/g, function (letter) {
                                return letter.toUpperCase();
                            });

                        if (record.result_reference_id != null) {
                            testName =
                                provider +
                                ": " +
                                record.result.name +
                                " #" +
                                record.result_reference_id;
                        } else {
                            testName = provider + ": " + record.result.name;
                        }
                    }
                    break;

                case "idexx":
                case "znlabs":
                case "midwest":
                case "nbiovet":
                case "vetek":
                case "microvet":
                case "bionote":
                case "heska":
                case "abaxis":
                case "heska_cloud":
                    if (Object.keys(record.test).length > 0) {
                        let itemName = "";
                        testName = "";

                        $.each(record.test, function (key, value) {
                            if (value.item) {
                                if (value.item.name) {
                                    itemName = value.item.name;
                                    testName +=
                                        '<div><a href="' +
                                        serviceURL +
                                        "inventory/item/view/" +
                                        value.item.encodedId +
                                        '">' +
                                        itemName +
                                        "</a></div>";
                                } else if (value.item.master_name) {
                                    itemName = value.item.master_name;
                                    testName += "<div>" + itemName + "</div>";
                                } else if (value.item.code_name) {
                                    itemName = value.item.code_name;
                                    testName += "<div>" + itemName + "</div>";
                                }
                            }
                        });
                    }
                    break;
                case "antech":
                    if (record.test && record.test.length > 0) {
                        testName = record.test;
                    }
                    break;
                default:
                    break;
            }

            return testName;
        },
        properTestDate(record) {
            let testDate = "";
            switch (record.module) {
                case "onprem":
                    if (record.test_date) {
                        testDate = this._setDateTimeDisplay(record.test_date);
                    }
                    break;

                case "idexx":
                case "znlabs":
                case "midwest":
                case "nbiovet":
                case "vetek":
                case "microvet":
                case "bionote":
                case "heska":
                case "abaxis":
                case "heska_cloud":
                    if (record.order_date) {
                        testDate = this._setDateTimeDisplay(record.order_date, (record.module == 'idexx') ? false : true);
                    }
                    break;

                case "antech":
                    if (record.order_received_datetime) {
                        testDate = this._setDateTimeDisplay(
                            record.order_received_datetime
                        );
                    }
                    break;
                default:
                    break;
            }

            return testDate;
        },
        properResultDate(record) {
            let resultDate = "";
            switch (record.module) {
                case "onprem":
                    break;
                case "abaxis_api":
                case "abaxis_fuse":
                    if (record.result_date) {
                        resultDate = this._setDateTimeDisplay(
                            record.result_date
                        );
                    }
                    break;
                case "lab":
                    if (record.result_date) {
                        resultDate = this._setDateTimeDisplay(
                            record.result_date
                        );
                    }
                    break;

                case "idexx":
                case "znlabs":
                case "midwest":
                case "nbiovet":
                case "vetek":
                case "microvet":
                case "bionote":
                case "heska":
                case "abaxis":
                case "heska_cloud":
                    if (record.result_received) {
                        resultDate = this._setDateTimeDisplay(
                            record.result_received
                        );
                    }
                    break;

                case "antech":
                    if (record.latest_result_received_datetime) {
                        resultDate = this._setDateTimeDisplay(
                            record.latest_result_received_datetime
                        );
                    }
                    break;
                default:
                    break;
            }
            return resultDate;
        },
        properPatientSpecies(record) {
            let species = "";

            if (!_.isEmpty(record.patient)) {
                species = record.patient.species;
            }

            return species ? species.toLowerCase() : "";
        },
        assignLab(record) {
            switch (record.module) {
                case "onprem":
                    this.$refs.modalAssignOnprem.open(record);
                    break;
                case "abaxis_api":
                    this.$refs.modalAssignAbaxisapi.open(record);
                    break;
                case "lab":
                case "idexx":
                case "znlabs":
                case "midwest":
                case "nbiovet":
                case "vetek":
                case "microvet":
                case "bionote":
                case "heska":
                case "heska_cloud":
                    this.$refs.modalAssignLab.open(record);
                    break;
                case "antech":
                    this.$refs.modalAssignAntech.open(record);
                    break;
            }
        },
        showPreviewResult(record) {
            let show = false;
            switch (record.module) {
                case "lab":
                case "idexx":
                case "znlabs":
                case "midwest":
                case "nbiovet":
                case "vetek":
                case "microvet":
                case "bionote":
                case "heska":
                case "heska_cloud":
                case "antech":
                case "abaxis_api":
                    show = true;
                    break;
            }
            return show;
        },
        deleteLab(record) {
            this.$refs.modalDelete.open({
                recordId: record.id.toString(),
                recordModule: record.module,
                businessId: this.businessId
            });
        },
        changePage(page) {
            this.fetchList("all", page);
        },
        previewResult(record) {
            this.$refs.modalPreviewResult.open(record);
        }
    }
};
</script>

<style scoped>
.initials {
    display: block;
    margin-bottom: 20px;
}
.list {
    margin: 0 15px 10px 15px;
}
.row-record {
    padding: 6px 0;
    border-bottom: 1px solid #ddd;
}
.row-record:hover {
    background-color: #f5f5f5;
}
.vet-pagination {
    margin-top: 50px;
}
</style>
